import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export type SpinnerAnimation = 'border' | 'grow';
export type SpinnerSize = 'sm';
export type SpinnerVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

const spinnerVariants = cva(
  'tw:inline-block tw:size-8 tw:align-[-0.125em] tw:rounded-full',
  {
    variants: {
      animation: {
        border:
          'tw:animate-shadcn-spinner-border tw:border-[0.25em] tw:border-solid tw:border-current tw:border-r-transparent',
        grow: 'tw:animate-shadcn-spinner-grow tw:bg-current tw:opacity-0',
      },
      size: {
        sm: 'tw:size-4',
      },
      variant: {
        primary: 'tw:text-primary',
        secondary: 'tw:text-secondary',
        success: 'tw:text-success',
        danger: 'tw:text-destructive',
        warning: 'tw:text-warning',
        info: 'tw:text-info',
        light: 'tw:text-light',
        dark: 'tw:text-dark',
      },
    },
    compoundVariants: [
      {
        animation: 'border',
        size: 'sm',
        className: 'tw:border-[0.2em]',
      },
    ],
    defaultVariants: {
      animation: 'border',
    },
  },
);

export type SpinnerProps = React.HTMLAttributes<HTMLElement> & {
  animation?: SpinnerAnimation;
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  as?: 'div' | 'span';
};

const Spinner: React.FC<SpinnerProps> = ({
  animation,
  size,
  variant,
  as: Component = 'div',
  className,
  ...props
}) => (
  <Component
    className={cn(spinnerVariants({ animation, size, variant }), className)}
    {...props}
  />
);
Spinner.displayName = 'Spinner';

export { Spinner, spinnerVariants };
